<script setup>
// Cores da marca (logo RD Nexus): no escuro o trilho é o azul-marinho do logo,
// com o ciano no módulo ativo; o laranja marca quem aguarda atendimento.
//
// Menu lateral do Nexus Chat: trilho de ícones com os módulos (Conversas,
// Contatos, Relatórios, Nexus IA, Configurações) e, ao lado, o
// painel do módulo aberto. O painel recolhe (escolha salva por pessoa); recolhido,
// passar o mouse num módulo abre os itens dele num menu flutuante. No celular,
// trilho e painel abrem juntos como gaveta pelo botão de menu do Chatwoot.
//
// Os módulos e itens são os do menu do Chatwoot (props.menuItems, montados no
// Sidebar.vue do core, com permissões e recursos), menos Captain, Campanhas,
// Central de ajuda, Chamadas e a Caixa de entrada (notificações). Em Conversas,
// os filtros fixos viram Minhas, Aguardando, Com a IA e Todas (ver
// abaDaLista.js e fila.js).
import { computed, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useWindowSize } from '@vueuse/core';
import { vOnClickOutside } from '@vueuse/components';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { useAccount } from 'dashboard/composables/useAccount';
import { usePolicy } from 'dashboard/composables/usePolicy';
import {
  provideSidebarContext,
  usePopoverState,
} from 'dashboard/components-next/sidebar/provider';
import Icon from 'next/icon/Icon.vue';
import SidebarAccountSwitcher from 'dashboard/components-next/sidebar/SidebarAccountSwitcher.vue';
import SidebarProfileMenu from 'dashboard/components-next/sidebar/SidebarProfileMenu.vue';
import SidebarCollapsedPopover from 'dashboard/components-next/sidebar/SidebarCollapsedPopover.vue';
import ComposeConversation from 'dashboard/components-next/NewConversation/ComposeConversation.vue';
import PainelDoModulo from './PainelDoModulo.vue';
import { abaAtual } from './abaDaLista';
import { useFila } from './fila';
import { encontraAtivo, folhasDe, visiveis } from './menu';

const props = defineProps({
  menuItems: { type: Array, required: true },
  isMobileSidebarOpen: { type: Boolean, default: false },
});

const emit = defineEmits([
  'closeMobileSidebar',
  'openKeyShortcutModal',
  'showCreateAccountModal',
]);

const { t } = useI18n({
  messages: {
    pt_BR: {
      NEUXS_MENU: {
        MINHAS: 'Minhas',
        AGUARDANDO: 'Aguardando',
        COM_IA: 'Com a IA',
        TODAS: 'Todas',
        RECOLHER: 'Recolher o menu',
        ABRIR: 'Abrir o menu',
        BUSCAR: 'Buscar',
      },
    },
    en: {
      NEUXS_MENU: {
        MINHAS: 'Mine',
        AGUARDANDO: 'Waiting',
        COM_IA: 'With AI',
        TODAS: 'All',
        RECOLHER: 'Collapse menu',
        ABRIR: 'Expand menu',
        BUSCAR: 'Search',
      },
    },
  },
});

const route = useRoute();
const router = useRouter();
const { accountScopedRoute } = useAccount();
const { shouldShow } = usePolicy();
const { uiSettings, updateUISettings } = useUISettings();
const { width } = useWindowSize();
const fila = useFila();

const LARGURA_DO_TRILHO = 56;
const OCULTOS = ['Captain', 'Campaigns', 'Portals', 'Calls', 'Inbox'];
// Ordem do trilho; módulo novo do core que não estiver aqui vem depois.
const ORDEM = [
  'Conversation',
  'Contacts',
  'Companies',
  'Reports',
  'NeuxsGestao',
  'NeuxsNexus',
];
const posicao = item =>
  ORDEM.includes(item.name) ? ORDEM.indexOf(item.name) : ORDEM.length;
const NO_RODAPE = ['Settings'];
const ICONES = {
  Conversation: 'i-lucide-messages-square',
  Contacts: 'i-lucide-contact-round',
  Companies: 'i-lucide-building-2',
  Reports: 'i-lucide-chart-column',
  NeuxsGestao: 'i-lucide-gauge',
  NeuxsNexus: 'i-lucide-bot',
  Settings: 'i-lucide-settings',
};

const eCelular = computed(() => width.value < 768);

// Recolhido: o que a pessoa escolheu; sem escolha, recolhe em tela pequena.
const recolhido = computed(() => {
  if (eCelular.value) return false;
  const escolha = uiSettings.value.neuxs_menu_recolhido;
  return typeof escolha === 'boolean' ? escolha : width.value < 1280;
});
const alternaRecolhido = () =>
  updateUISettings({ neuxs_menu_recolhido: !recolhido.value });

// Mesma regra de permissão do menu do core (rota → permissões e recurso).
const permitido = to => {
  const { meta = {} } = router.resolve(to);
  return shouldShow(
    meta.featureFlag || '',
    meta.permissions || [],
    meta.installationTypes || []
  );
};

const nasRotasDaLista = aba =>
  ['home', 'inbox_conversation'].includes(route.name) && abaAtual.value === aba;

const filtrosDeConversa = computed(() => {
  const filtro = (name, aba, icon, extra = {}) => ({
    name,
    icon,
    to: accountScopedRoute('home', {}, { aba }),
    ativoQuando: () => nasRotasDaLista(aba),
    ...extra,
  });
  const timeIa = fila.value.time_ia_id;

  return [
    filtro('NeuxsMinhas', 'me', 'i-lucide-user-round', {
      label: t('NEUXS_MENU.MINHAS'),
    }),
    filtro('NeuxsAguardando', 'unassigned', 'i-lucide-hourglass', {
      label: t('NEUXS_MENU.AGUARDANDO'),
      badgeCount: fila.value.aguardando,
      destaque: true,
    }),
    ...(timeIa
      ? [
          {
            name: 'NeuxsComIa',
            label: t('NEUXS_MENU.COM_IA'),
            icon: 'i-lucide-bot',
            to: accountScopedRoute(
              'team_conversations',
              { teamId: timeIa },
              { aba: 'all' }
            ),
            ativoQuando: () =>
              ['team_conversations', 'conversations_through_team'].includes(
                route.name
              ) && String(route.params.teamId) === String(timeIa),
          },
        ]
      : []),
    filtro('NeuxsTodas', 'all', 'i-lucide-list', {
      label: t('NEUXS_MENU.TODAS'),
    }),
  ];
});

const SAEM_DE_CONVERSAS = ['All', 'Participating', 'Unattended', 'Mentions'];

const modulos = computed(() =>
  props.menuItems
    .filter(item => !OCULTOS.includes(item.name))
    .map(item => {
      const icon = ICONES[item.name] || item.icon;
      if (item.name !== 'Conversation') return { ...item, icon };
      return {
        ...item,
        icon,
        children: [
          ...filtrosDeConversa.value,
          ...item.children.filter(
            filho => !SAEM_DE_CONVERSAS.includes(filho.name)
          ),
        ],
      };
    })
    .map(item =>
      item.children
        ? { ...item, children: visiveis(item.children, permitido) }
        : item
    )
    .filter(item =>
      item.children ? item.children.length > 0 : item.to && permitido(item.to)
    )
    .sort((a, b) => posicao(a) - posicao(b))
);

const doTopo = computed(() =>
  modulos.value.filter(item => !NO_RODAPE.includes(item.name))
);
const doRodape = computed(() =>
  modulos.value.filter(item => NO_RODAPE.includes(item.name))
);

const resolvePath = to => router.resolve(to)?.path || '/';

const ativoDo = modulo =>
  modulo.children
    ? encontraAtivo(folhasDe(modulo.children), route, resolvePath)
    : undefined;

const moduloDaRota = computed(() =>
  modulos.value.find(modulo =>
    modulo.children
      ? ativoDo(modulo)
      : modulo.activeOn?.includes(route.name) ||
        route.path === resolvePath(modulo.to)
  )
);

// O módulo do painel é o da página aberta.
const moduloAberto = computed(() => moduloDaRota.value || modulos.value[0]);
const itemAtivo = computed(() =>
  moduloAberto.value ? ativoDo(moduloAberto.value) : undefined
);

const primeiraRota = modulo =>
  modulo.to || folhasDe(modulo.children).find(folha => folha.to)?.to;

// Como no menu do core: o módulo abre no primeiro item que a pessoa pode ver.
const abreModulo = modulo => {
  if (moduloDaRota.value?.name !== modulo.name) {
    router.push(primeiraRota(modulo));
  }
};

const contagemDo = modulo => {
  if (modulo.name === 'Conversation') return fila.value.aguardando;
  return 0;
};

// Menu flutuante do trilho recolhido (o mesmo do menu recolhido do core).
const expandedItem = ref(null);
provideSidebarContext({
  expandedItem,
  setExpandedItem: name => {
    expandedItem.value = name;
  },
  isCollapsed: ref(false),
  sidebarWidth: ref(LARGURA_DO_TRILHO),
  isResizing: ref(false),
});
const {
  activePopover,
  setActivePopover,
  closeActivePopover,
  scheduleClose,
  cancelClose,
} = usePopoverState();
const posicaoDoFlutuante = ref(null);

const mostraFlutuante = (modulo, evento) => {
  if (!recolhido.value || !modulo.children) return;
  cancelClose();
  posicaoDoFlutuante.value = evento.currentTarget.getBoundingClientRect();
  setActivePopover(`neuxs:${modulo.name}`);
};
const escondeFlutuante = () => scheduleClose(200);

const fecha = () => {
  if (props.isMobileSidebarOpen) emit('closeMobileSidebar');
};
watch(() => route.fullPath, fecha);
</script>

<template>
  <aside
    v-on-click-outside="[
      fecha,
      {
        ignore: [
          '#mobile-sidebar-launcher',
          '[data-popover-content]',
          '[data-popover-backdrop]',
        ],
      },
    ]"
    class="flex h-full text-sm z-50 fixed top-0 ltr:left-0 rtl:right-0 max-w-[85vw] md:relative md:z-40 md:max-w-none md:flex-shrink-0 transition-transform duration-200 ease-out md:ltr:translate-x-0 md:rtl:translate-x-0"
    :class="{
      'ltr:-translate-x-full rtl:translate-x-full': !isMobileSidebarOpen,
      'shadow-xl md:shadow-none': isMobileSidebarOpen,
    }"
  >
    <!-- Trilho -->
    <nav
      class="flex flex-col items-center w-14 flex-shrink-0 py-3 gap-1 bg-n-brand text-white dark:bg-[#12284a] dark:ltr:border-r dark:rtl:border-l dark:border-white/5"
    >
      <div class="mb-2 rounded-xl bg-white">
        <SidebarAccountSwitcher
          is-collapsed
          @show-create-account-modal="emit('showCreateAccountModal')"
        />
      </div>
      <button
        v-for="modulo in doTopo"
        :key="modulo.name"
        type="button"
        :title="modulo.label"
        class="relative grid place-content-center size-10 rounded-xl transition-colors duration-100"
        :class="
          moduloAberto?.name === modulo.name
            ? 'bg-white/25 text-white dark:bg-[#50c0e0]/15 dark:text-[#7fd3ec]'
            : 'text-white/75 hover:bg-white/15 hover:text-white dark:text-white/60'
        "
        @click="abreModulo(modulo)"
        @mouseenter="mostraFlutuante(modulo, $event)"
        @mouseleave="escondeFlutuante"
      >
        <Icon :icon="modulo.icon" class="size-5" />
        <span
          v-if="contagemDo(modulo)"
          class="absolute -top-0.5 ltr:-right-0.5 rtl:-left-0.5 min-w-4 h-4 px-1 grid place-content-center rounded-full text-[10px] font-medium leading-none"
          :class="
            modulo.name === 'Conversation'
              ? 'bg-[#f08820] text-white'
              : 'bg-white text-n-brand dark:text-[#12284a]'
          "
        >
          {{ contagemDo(modulo) > 99 ? '99+' : contagemDo(modulo) }}
        </span>
      </button>
      <div class="flex-1" />
      <button
        v-if="!eCelular"
        type="button"
        :title="recolhido ? t('NEUXS_MENU.ABRIR') : t('NEUXS_MENU.RECOLHER')"
        class="grid place-content-center size-10 rounded-xl text-white/75 hover:bg-white/15 hover:text-white dark:text-white/60"
        @click="alternaRecolhido"
      >
        <Icon
          :icon="
            recolhido ? 'i-lucide-panel-left-open' : 'i-lucide-panel-left-close'
          "
          class="size-5"
        />
      </button>
      <button
        v-for="modulo in doRodape"
        :key="modulo.name"
        type="button"
        :title="modulo.label"
        class="grid place-content-center size-10 rounded-xl transition-colors duration-100"
        :class="
          moduloAberto?.name === modulo.name
            ? 'bg-white/25 text-white dark:bg-[#50c0e0]/15 dark:text-[#7fd3ec]'
            : 'text-white/75 hover:bg-white/15 hover:text-white dark:text-white/60'
        "
        @click="abreModulo(modulo)"
        @mouseenter="mostraFlutuante(modulo, $event)"
        @mouseleave="escondeFlutuante"
      >
        <Icon :icon="modulo.icon" class="size-5" />
      </button>
      <div class="mt-1">
        <SidebarProfileMenu
          is-collapsed
          @open-key-shortcut-modal="emit('openKeyShortcutModal')"
        />
      </div>
    </nav>

    <!-- Painel do módulo -->
    <section
      v-if="!recolhido && moduloAberto?.children"
      class="flex flex-col w-56 flex-shrink-0 bg-n-background ltr:border-r rtl:border-l border-n-weak min-w-0"
    >
      <header class="flex items-center gap-1 px-3 pt-3 pb-2">
        <h2 class="flex-1 text-sm font-medium text-n-slate-12 truncate">
          {{ moduloAberto.label }}
        </h2>
        <router-link
          :to="{ name: 'search' }"
          :title="t('NEUXS_MENU.BUSCAR')"
          class="grid place-content-center size-7 rounded-lg text-n-slate-11 hover:bg-n-alpha-2"
        >
          <Icon icon="i-lucide-search" class="size-4" />
        </router-link>
        <ComposeConversation
          v-if="moduloAberto.name === 'Conversation'"
          align="start"
        >
          <template #trigger>
            <button
              type="button"
              class="grid place-content-center size-7 rounded-lg text-n-slate-11 hover:bg-n-alpha-2"
            >
              <Icon icon="i-lucide-pen-line" class="size-4" />
            </button>
          </template>
        </ComposeConversation>
      </header>
      <div class="flex-1 overflow-y-auto no-scrollbar px-2 pb-4">
        <PainelDoModulo
          :modulo="moduloAberto"
          :ativo="itemAtivo"
          @navegou="fecha"
        />
      </div>
    </section>

    <template v-for="modulo in modulos" :key="`flutuante-${modulo.name}`">
      <SidebarCollapsedPopover
        v-if="
          recolhido &&
          modulo.children &&
          activePopover === `neuxs:${modulo.name}`
        "
        :label="modulo.label"
        :children="modulo.children"
        :active-child="ativoDo(modulo)"
        :trigger-rect="posicaoDoFlutuante"
        @close="closeActivePopover"
        @mouseenter="cancelClose"
        @mouseleave="escondeFlutuante"
      />
    </template>
  </aside>
</template>
